import React from 'react';
import { Box, Grid, Paper, TableContainer } from '@mui/material';
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TablePagination from '@mui/material/TablePagination';
import TableRow from "@mui/material/TableRow";
import Fuse from 'fuse.js';
import { getComparatorSort } from '../../helpers';
import { LIST_NULL_OPTION_VALUE, PLAYLIST_FILTERS_OPTIONS } from '../../helpers/constant';
import SearchBar from '../search/searchbar';
import TableFilter from './filters';
import TableHeader from './header';
import TablePaginationActions from './pagination-actions';
import TableLoader from './table-loader';

const TableComponent = ({ spinner, columns, data, noDataMessage, rowClick, rowStyle, defaultOptions, pagination, searchKeys = [], filterTypes = {}, defaultFilterValues = {}, companyListOptions = [], headerActionButtons, totalCount = null, serverPaginate = false, onPageChange = () => {}, currentPage = 0, hasNextPage = '', onFilter = () => {} }) => {
    const [visibleRows, setVisibleRows] = React.useState([...data]);
    const [filterValues, setFilterValues] = React.useState(defaultFilterValues); // Initialize with default filter values
    const [searchValue, setSearchValue] = React.useState("");
    const [pageInfo, setPageInfo] = React.useState({
        page: currentPage,
        rowsPerPage: defaultOptions?.rowPerPage || 10,
        sortKey: defaultOptions?.sortKey || "",
        order: defaultOptions?.sortOrder || "",
    });
    const muiTableContainerRef = React.useRef(null);
    const previousFilterValues = React.useRef(defaultFilterValues);
    const previousPageInfo = React.useRef({ page: currentPage, rowsPerPage: defaultOptions?.rowPerPage || 10, sortKey: defaultOptions?.sortKey || "", order: defaultOptions?.sortOrder || "" });

    const scrollTop = () => {
        if (muiTableContainerRef.current) {
            muiTableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleChangePage = (event, newPage) => {
        // on page change scroll to top of the table
        scrollTop();
        setPageInfo((prev) => ({
            ...prev,
            page: newPage,
        }));
    };

    const handleChangeRowsPerPage = (event) => {
        // on page change scroll to top of the table
        scrollTop();
        setPageInfo((prev) => ({
            ...prev,
            page: 0,
            rowsPerPage: +event.target.value,
        }));
    };

    const handleSort = (property, newOrder) => {
        // on page change scroll to top of the table
        scrollTop();
        const isAsc = newOrder === 'asc';
        setPageInfo((prev) => ({
            ...prev,
            page: 0,
            sortKey: property,
            order: isAsc ? 'desc' : 'asc',
        }));
    };

    const sortedRows = React.useMemo(() => {
        if (searchKeys.length === 0) { // More clear
            return [...data];
        }
        return [...data].sort(getComparatorSort(pageInfo.order, pageInfo.sortKey));
    }, [pageInfo, data, searchKeys.length]);

    const fuse = React.useMemo(() => {
        return new Fuse(sortedRows, {
            keys: searchKeys || [],
            threshold: 0.2,
        });
    }, [sortedRows, searchKeys]);

    React.useEffect(() => {
        const list = searchValue.trim() ? fuse.search(searchValue).map(result => result.item) : sortedRows;
        setVisibleRows([...list]);
    }, [searchValue, sortedRows]);

    // update filterValues
    const handleFilters = (values) => {
        let hasChanged = false;
        for (const key in values) {
            if (previousFilterValues.current[key] !== values[key]) {
                hasChanged = true;
                break;
            }
        }
        if (hasChanged) {
            previousFilterValues.current = values;
            if (serverPaginate) {
                const filter = {};
                Object.keys(filterTypes).forEach((key) => {
                    if (values[key]) {
                        filter[filterTypes[key]] = values[key];
                    }
                });
                setPageInfo((prev) => ({
                    ...prev,
                    page: 0,
                    REFRESH_DATA: true, // Indicate that data needs to be refreshed 
                }));
                onFilter(filter);
            }
            setFilterValues(values);
        }
    };

    const filteredRows = React.useMemo(() => {
        if (serverPaginate) {
            return visibleRows; // Return visible rows without filtering for server-side pagination
        } else {
            return visibleRows.filter((row) => {
                for (const key in filterTypes) {
                    // custom code written for playlist
                    if (key === "playListBookmark" && filterValues[key] !== undefined && filterValues[key] !== PLAYLIST_FILTERS_OPTIONS[0] && !row[filterTypes[key]].includes(filterValues[key])) {
                        return false;
                    }
                    if (key !== "playListBookmark" && filterValues[key] !== undefined && filterValues[key] !== LIST_NULL_OPTION_VALUE && row[filterTypes[key]] !== filterValues[key]) {
                        return false;
                    }
                }
                return true;
            });
        }
    }, [visibleRows, filterValues, filterTypes, serverPaginate]);

    const getListData = React.useCallback(() => {
        if (!pagination) return filteredRows;
        return filteredRows.slice(pageInfo.page * pageInfo.rowsPerPage, pageInfo.page * pageInfo.rowsPerPage + pageInfo.rowsPerPage);
    }, [filteredRows, pageInfo.page, pageInfo.rowsPerPage, pagination]);

    const finalVisibleRows = React.useMemo(() => {
        return getListData();
    }, [getListData]);

    React.useEffect(() => {
        if(hasNextPage === false && data.length === 0 && currentPage === 0 && pageInfo.page !== currentPage) {
            console.log("Resetting page to 0 due to currentPage change");
            setPageInfo((prev) => ({
                ...prev,
                page: currentPage,
            }));
        }
    }, [currentPage, hasNextPage, pageInfo.page, spinner, data]);

    React.useEffect(() => {
        if (serverPaginate) {
            const pageInfoUpdated = Object.keys(pageInfo).some(key => previousPageInfo.current[key] !== pageInfo[key]);
            if (pageInfoUpdated) {
                const hasMoreData = sortedRows.length < (pageInfo.page + 1) * pageInfo.rowsPerPage;
                const itemsCountChanged = (previousPageInfo.current.rowsPerPage !== pageInfo.rowsPerPage)
                if (hasMoreData || itemsCountChanged) {
                    pageInfo.REFRESH_DATA = itemsCountChanged;
                    onPageChange(pageInfo);
                }
            }
            previousPageInfo.current = { ...pageInfo };
        }
    }, [pageInfo, serverPaginate]);

    return (
        <Grid
            className="vocalysd-list-table"
            display="flex"
            flexDirection="column"
            gap={2}
            height="100%"
        >
            <Box
                display="flex"
                flexWrap="wrap"
                flexDirection="row"
                justifyContent="space-between"
                gap={2}
            >
                <Box
                    display="flex"
                    flexWrap="wrap"
                    flexDirection="row"
                    justifyContent="space-between"
                    gap={2}
                >
                    {searchKeys?.length ?
                        <SearchBar
                            callBack={(_searchValue) => {
                                if(_searchValue === searchValue) {
                                    return;
                                }
                                if (pageInfo.page !== 0) {
                                    console.log("Resetting page to 0 due to search or sort change");
                                    setPageInfo((prev) => ({ ...prev, page: 0 }));
                                }
                                setSearchValue(_searchValue)
                            }}
                        /> : ""}
                    {Object.keys(filterTypes).length ? <TableFilter
                        spinner={spinner}
                        filterTypes={filterTypes}
                        defaultFilterValues={defaultFilterValues}
                        companyListOptions={companyListOptions}
                        onChange={handleFilters}
                    /> : ""}
                </Box>
                {headerActionButtons?.()}
            </Box>
            <Paper
                sx={{display: "flex", flexDirection: "column", flexGrow: 1, width: '100%', overflow: 'hidden', borderRadius: 2.5 }}
            >
                <TableContainer sx={{ maxHeight: "100%", flexGrow: 1 }} ref={muiTableContainerRef}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHeader
                            columns={columns}
                            order={pageInfo.order}
                            orderBy={pageInfo.sortKey}
                            onRequestSort={handleSort}
                        />
                        <TableBody>
                            {spinner ? (
                                <TableLoader columnsLength={columns.length} />
                            ) : (
                                finalVisibleRows.map((item, index) => (
                                    <TableRow hover key={index} sx={{
                                        cursor: rowClick ? "pointer" : "initial",
                                        ...rowStyle
                                    }} onClick={() => rowClick ? rowClick(item) : {}}>
                                        {columns.map((c, indexColumn) => (
                                            <TableCell key={index + "-" + indexColumn} onClick={item.handleClick} align={c.align} sx={c.cellStyle}
                                                className={c.cellClassName ? c.cellClassName(item) : ""}>
                                                {c.renderItem(item, index, indexColumn)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                            {!spinner && !filteredRows?.length ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length}>
                                        {noDataMessage}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                ""
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pagination ? <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={(searchValue.trim() ? filteredRows.length : totalCount) ?? filteredRows.length}
                    rowsPerPage={pageInfo.rowsPerPage}
                    page={pageInfo.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    disabled={spinner}
                    ActionsComponent={TablePaginationActions}
                    showFirstButton={serverPaginate ? false : true}
                    showLastButton={serverPaginate ? false : true}
                    sx={{
                        '& .MuiToolbar-root': {
                            flexWrap: 'wrap',
                            px: 2,
                            py: 1,
                            justifyContent: {
                                xs: 'center',
                                sm: 'end',
                            },
                        },
                    }}
                /> : ""}
            </Paper>
        </Grid>
    );
};

export default React.memo(TableComponent);